import { Link } from "react-router-dom";
import { termsReady } from "../lib/termsOfUse";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import AccountProfileCard from "../components/AccountProfileCard";
import PasskeysCard from "../components/PasskeysCard";
import SendFeedbackCard from "../components/SendFeedbackCard";
import TextSizeCard from "../components/TextSizeCard";
import NotificationsCard from "../components/NotificationsCard";
import UsageDisclosureCard from "../components/UsageDisclosureCard";
import { BUILD_STAMP } from "../buildInfo";
import HealthInfoCard from "../components/HealthInfoCard";
import CoachProfileView from "../components/CoachProfileView";
import CoachAccessInviteActions from "../components/CoachAccessInviteActions";
import AddPlayerCard from "../components/AddPlayerCard";
import { hasCoachProfileContent } from "../lib/coachProfile";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, X } from "lucide-react";
import { newUi } from "../lib/featureFlags";
import SettingsScreen from "../components/SettingsScreen";

/**
 * `/settings` — THE account page, and since #338 the only one.
 *
 * WHAT #338 DID, AND WHY. Martin's decision 4 on #287 (Discord,
 * 2026-08-18): "one page". Until then the app had `/settings` and
 * `/profile`, both about "your account", each with a button pointing at the
 * other — #318's deliberate "both doors, no redirect" IA. Two pages meant
 * two places to look for the same kind of thing, and — the reason it was a
 * decision rather than a preference — a live risk of divergent read gates,
 * with #287's health card about to land on one of them. `/profile` is now a
 * pure redirect here (App.js) and Profile.jsx is gone.
 *
 * SECTION ORDER IS THE ARGUMENT, so it is stated rather than inherited:
 *
 *   1. Account & profile  — ONE section since #456's decision comment
 *                           (Andreas, 2026-08-27): "We want one section in
 *                           settings, both profile and account as one
 *                           area." It holds everything the two former
 *                           cards held — the identity you sign IN with
 *                           (name, email, role), the identity other people
 *                           SEE (the player's cricket profile, or the
 *                           coach's coaching profile), and the control
 *                           that ends the session. This file used to
 *                           argue, here, for keeping those two cards
 *                           adjacent-but-separate ("the same question
 *                           asked of the other audience"); that adjacency
 *                           rationale is exactly why they are now one
 *                           area, and the decision superseding it is the
 *                           product owner's, recorded on #456. The merged
 *                           card lives in
 *                           `components/AccountProfileCard.jsx`, with the
 *                           argument for it staying ALWAYS OPEN rather
 *                           than folding.
 *   2. Coach access       — WHO is linked, i.e. who the profile's audience
 *                           is.
 *   3. Health details     — WHAT those links share. Below the card that
 *                           decides the links, per #287.
 *   4. Text size / contrast, 5. Notifications — how the APP behaves for
 *                           you. Not identity, so not interleaved with it;
 *                           #338 moved these below the identity group.
 *   6. Send feedback, 7. What we measure, 8. About — meta, at the bottom.
 *                           #384 item 4 shrank About from a card to one line
 *                           and swapped the order: it is the build stamp you
 *                           quote while filling in the form above it. #224's
 *                           usage disclosure went between them because it is
 *                           the same kind of sentence as the feedback card's
 *                           "Sent with your name and the screen you're on" —
 *                           what leaves this device, said plainly — and this
 *                           page is where the app already keeps those.
 *
 * #456 (player) / #459 (coach) FOLDED FIVE OF THESE, AND MOVED TWO OUT.
 * Health and emergency contact, Text size, Notifications, Send feedback and
 * What we measure are collapsible `SectionCard`s that arrive CLOSED,
 * each with a header state saying what is inside it (`lib/settingsSummary.js`).
 * Folding changed how much of the page a section costs, not where it sits.
 * Text size and Notifications moved into `components/TextSizeCard.jsx` and
 * `components/NotificationsCard.jsx` in the same slice: this file was 614
 * lines serving BOTH roles from one component, and the alternative to
 * extracting was growing it further. The Account/profile merge moved the
 * account state out the same way — this component now composes sections
 * and owns none of their forms.
 *
 * WHAT #456 AND #459 STILL LEAVE OPEN, so the next reader does not assume
 * it was missed:
 *   - Issue #975's own audit of this comment (2026-09-30): the "Mobile
 *     phone" FIELD bullet that used to be here is stale. The schema
 *     decision it said was still needed was made and shipped: the account
 *     now stores a mobile number (`AccountProfileCard.jsx:112-119`, `PUT
 *     /auth/me` with `mobile_phone`), and it is private to the player —
 *     not shown to a coach, not on any admin read, and listed as such in
 *     `lib/privacyNotice.js`. The mobile-phone NOTIFICATION channel, which
 *     persists nothing either way, is a separate thing and shipped earlier
 *     (NotificationsCard.jsx).
 *   - Searching for a coach and inviting them from their profile (#456 →
 *     tracked as #381/#288). A separate feature, not a settings-layout
 *     change. The coach photo upload #459 asked for landed as #478/#499
 *     (CoachProfileCard.jsx), and the coach "Add player" shortcut (#459)
 *     landed as #530 (AddPlayerCard.jsx, mounted at the top below).
 *
 * Roles: 1's profile half is player-or-coach (an admin has neither a
 * cricket profile nor a coaching one, and #338 deliberately did NOT port
 * Profile.jsx's "an admin account is a platform role" note — that paragraph
 * existed to explain why a page called Profile was nearly empty, and a page
 * that never promised an admin a profile has nothing to explain; an admin's
 * section 1 is the account fields and sign-out alone). 2 and 3 are
 * player-only.
 */
export default function Settings() {
  const { user } = useAuth();

  // Issue #975 (D186.8/D186.9) — Settings A, behind the `settings` flag,
  // players only at first. FIRST STATEMENT after `useAuth`, on purpose:
  // everything below this line — every component this file renders and
  // every branch in CoachAccessCard — must stay byte-identical on the
  // flag-off leg, for an admin, and (until #976) for a coach (D186.9:
  // "admins aren't drawn and keep the shipped page"). A gate placed lower,
  // or one that touched a hook's call order, would risk exactly the
  // divergence this early return exists to rule out — `Settings.test.js`
  // pins this line's position.
  //
  // Issue #976 (Settings B, D186.8) widens the role check from "player" to
  // "player or coach" — SettingsScreen itself now branches on role for the
  // coach variant (Add player first, no coach-access card, no health fold,
  // D186.9's admin exclusion is unaffected — an admin's role is neither).
  if (newUi("settings") && (user?.role === "player" || user?.role === "coach")) return <SettingsScreen />;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Preferences</p>
        <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-900 mt-1">Settings</h1>
      </div>

      {/* Issue #530 — the ONE exception to the section order argued above,
          and it is role-scoped: for a COACH, Martin's #459 fourth bullet
          asked for an "Add Player" shortcut "at the top", so the coach's
          invite entry point sits above even Account & profile. Always open
          rather than a #475 fold on purpose — a shortcut that arrives
          folded is not a shortcut; the decision is argued on the component
          (components/AddPlayerCard.jsx), along with the enumeration
          posture its ACK inherits and the outbound-invite list it carries
          (the complement of CoachPortal's inbound-only pending card). */}
      {user?.role === "coach" && <AddPlayerCard />}

      {/* Section 1 — see the order note above. One card, all roles; the
          role decides which profile half it carries. */}
      <AccountProfileCard />

      {/* Issue #560 — passkey enrolment, an AccountProfileCard SIBLING:
          a passkey is a sign-in credential, so it sits in the identity
          group directly under the account card rather than growing that
          already-merged card a third concern. All roles — every account
          signs in. The confined (must_change_password) state renders its
          refusal honestly inside the card (see PasskeysCard.jsx). */}
      <PasskeysCard />

      {user?.role === "player" && <CoachAccessCard />}

      {/* Issue #287 — Martin's "under your account settings should also be
          fields outlining any medical conditions", plus the emergency
          contact he added on 2026-08-18. Player role only, and below the
          Coach access card deliberately: the card that decides WHO is
          linked sits above the card whose contents those links share. A
          coach has no player profile, so the API 404s them on
          /players/me/health — showing them an empty health form they can
          never save is #283's defect. */}
      {user?.role === "player" && <HealthInfoCard />}

      <TextSizeCard />

      <NotificationsCard />

      <SendFeedbackCard />

      <UsageDisclosureCard />

      {/* Issue #384 item 4 (Andreas's decision, 2026-08-20): KEEP the About
          section, make it small. Martin asked whether it was needed; it is,
          for one reason — "which version were you on" is unanswerable
          without it when someone reports a problem in person or on Discord.
          (#139's feedback form attaches the stamp automatically, so a FILED
          report already carries it; this is for the conversations that never
          become one.)

          So it stops being a full card with a heading and a strapline the
          rest of the app already says, and becomes the one line that carries
          the fact. It also stops LYING while it is here: it read
          "CricketCompass v1.0", a hand-written number that has never been
          this app's version — the same class of stale copy the labels
          section of CLAUDE.md is about. `BUILD_STAMP` is generated at build
          time (scripts/generate-build-info.js) from the real version and
          commit, which is the string worth quoting back at us.

          Last on the page, below Send feedback, because it is what you read
          WHILE filling that in. */}
      <p className="text-xs text-slate-500 px-1" data-testid="settings-about">
        CricketCompass <span data-testid="settings-build-stamp" className="font-mono">{BUILD_STAMP}</span>
        {" — quote this build if you report a problem."}
      </p>

      {/* Issue #449 (interim, partial): the same door as the usage
          disclosure above it, for the same reason — everyone signed in
          reaches this page, and it is where the app already keeps its
          statements about what leaves this device. */}
      <p className="text-xs text-slate-500 px-1" data-testid="settings-privacy-link">
        <Link to="/privacy" state={{ from: "/settings" }} className="underline hover:text-slate-700">
          Privacy notice
        </Link>
      </p>
      {/* Issue #1340 (D367.3): once the terms text exists. */}
      {termsReady() && (
        <p className="text-xs text-slate-500 px-1" data-testid="settings-terms-link">
          <Link to="/terms" state={{ from: "/settings" }} className="underline hover:text-slate-700">
            Terms of use
          </Link>
        </p>
      )}
    </div>
  );
}

/**
 * Issue #138 rework (spec §5): the player-facing half of the consent-based
 * coach share flow. A player invites a coach by exact email — same
 * enumeration-safe route as before (`POST /coach-links`, byte-identical
 * response for hit/non-coach/miss) — but the link now starts `invited`, not
 * `active`; the coach has to accept it (their half of this flow lives in
 * CoachPortal.jsx) before anything is actually shared. This card is what
 * lets the player tell "invite sent, waiting on them" apart from "they've
 * accepted, my data is now visible to them" — `status` is the only signal
 * for that distinction, so both states get a plainly different badge.
 *
 * Revoked links are deliberately left out of the list (not fetched-and-
 * hidden, just not rendered) — a revoked/declined pairing is, from this
 * card's point of view, the same as "no coach" and would only clutter a
 * settings page with dead rows; the player can always send a fresh invite,
 * which reuses the same underlying row server-side (`upsertInvitedCoachLink`).
 */
function CoachAccessCard() {
  const [links, setLinks] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState(null);

  /**
   * Issue #290: until now this card showed a coach's name and email and
   * nothing else — the entire coach identity the product exposed. Each row
   * now also carries whatever that coach wrote about themselves.
   *
   * Fetched per link rather than joined into `GET /coach-links`: a
   * dedicated route and serialiser, per #290's citation of #287's
   * `toApiPlayer` trap (a serialiser that grows a field for one caller
   * hands it to every other caller of that serialiser, forever).
   *
   * INVITED ROWS GET IT TOO, and that is the point rather than a
   * side-effect — `GET /api/coach-profiles/:id` accepts an `invited` link
   * precisely because a pairing that is still pending is the one moment
   * the player has a decision in front of them. A 404 here is ordinary
   * (that coach has written nothing), so it is swallowed to `undefined`
   * and the row renders as it always did.
   */
  const loadProfiles = (rows) => {
    Promise.all(
      rows.map((l) =>
        api
          .get(`/coach-profiles/${l.coach_id}`)
          .then((r) => [l.coach_id, r.data])
          .catch(() => [l.coach_id, null]),
      ),
    ).then((pairs) => setProfiles(Object.fromEntries(pairs)));
  };

  const loadLinks = () =>
    api
      .get("/coach-links")
      .then((r) => {
        const rows = r.data.filter((l) => l.status !== "revoked");
        setLinks(rows);
        loadProfiles(rows);
      })
      .catch(() => setLinks([]))
      .finally(() => setLoading(false));

  // Mount only. `loadLinks` is re-created every render (it now closes over
  // `loadProfiles`), so listing it would re-fetch the whole card on every
  // keystroke in the invite field — the same reason GroundPicker.jsx and
  // TrainingSessions.jsx suppress this rule.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLinks(); }, []);

  const sendInvite = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const { data } = await api.post("/coach-links", { coach_email: trimmed });
      // Deliberately generic — see routes/coachLinks.ts's enumeration note.
      // This UI must never claim to know whether the email matched a coach.
      toast.success(data.detail);
      setEmail("");
      loadLinks();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send invite");
    }
    setSending(false);
  };

  const revoke = async (link) => {
    setBusyId(link.id);
    try {
      await api.delete(`/coach-links/${link.id}`);
      toast.success(link.status === "invited" ? "Invite withdrawn" : "Coach access removed");
      loadLinks();
    } catch {
      toast.error("Failed");
    }
    setBusyId(null);
  };

  return (
    <Card className="border-slate-200" data-testid="settings-coach-access-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#0B2E5915" }}>
            <Users size={16} color="#0B2E59" />
          </div>
          <div>
            <h2 className="font-display font-bold text-lg text-slate-900">Coach access</h2>
            <p className="text-sm text-slate-500">Share your performance data with a coach by their account email. The coach will see your name and can accept or decline. They can't see any of your cricket data unless they accept.</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="coach@example.com"
            className="h-11 flex-1"
            data-testid="settings-coach-invite-email"
            onKeyDown={(e) => { if (e.key === "Enter") sendInvite(); }}
          />
          <Button
            onClick={sendInvite}
            disabled={sending || !email.trim()}
            className="h-11 sm:w-auto"
            style={{ background: "#0B2E59" }}
            data-testid="settings-coach-invite-send"
          >
            {sending ? "Sending…" : "Send invite"}
          </Button>
        </div>

        {!loading && links.length > 0 && (
          <div className="space-y-2 pt-1" data-testid="settings-coach-links">
            {links.map((l) => (
              <div key={l.id} className="rounded-lg border border-slate-200 p-3" data-testid={`settings-coach-link-${l.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{l.coach_display_name || l.coach_email}</div>
                    <div className="text-xs text-slate-500 truncate">{l.coach_email}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge
                      className="text-[10px]"
                      style={
                        l.status === "active"
                          ? { background: "#16A34A20", color: "#16A34A" }
                          : { background: "#D4A01720", color: "#8a6a10" }
                      }
                      data-testid={`settings-coach-link-status-${l.id}`}
                    >
                      {/* #506: a coach-initiated invite is a question FOR
                          this player, not a status of something they did —
                          the badge says which way it points. */}
                      {l.status === "active"
                        ? "Active"
                        : l.initiated_by === "coach"
                          ? "Invited you"
                          : "Invite pending"}
                    </Badge>
                    {l.status === "invited" && l.initiated_by === "coach" ? (
                      /* #506: the row is a pending DECISION — accept/decline
                         replaces the withdraw ✕, which belongs to invites
                         the player sent. Decline uses the same DELETE the ✕
                         does; the split is presentation, not capability. */
                      <CoachAccessInviteActions link={l} onDone={loadLinks} />
                    ) : (
                      <button
                        onClick={() => revoke(l)}
                        disabled={busyId === l.id}
                        title={l.status === "invited" ? "Withdraw invite" : "Remove coach"}
                        className="h-11 w-11 sm:h-9 sm:w-9 flex items-center justify-center text-slate-400 hover:text-red-600 disabled:opacity-50"
                        data-testid={`settings-coach-link-revoke-${l.id}`}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>
                {/* #290. A bare name-and-email row is still the honest
                    default when the coach has written nothing. The gate is
                    `hasCoachProfileContent`, NOT the truthiness of the
                    fetched object: a coach who wrote a profile and then
                    blanked every field still has a row, so the route
                    answers 200 with an all-null body (only a never-written
                    profile 404s). CoachProfileView returns null for that
                    body, but this wrapper would already have drawn its
                    divider and padding around the nothing. Same helper
                    Profile.jsx gates its own empty state on, so the two
                    surfaces cannot disagree about what "empty" means. */}
                {hasCoachProfileContent(profiles[l.coach_id]) && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <CoachProfileView
                      profile={profiles[l.coach_id]}
                      compact
                      name={l.coach_display_name}
                      testId={`settings-coach-profile-${l.id}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
